import { useCallback, useEffect, useRef, useState } from 'react';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    ImagePlusIcon,
    Loader2Icon,
    Trash2Icon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
    useAddHubImage,
    useDeleteHubImage,
    useHubImages,
} from '@/services/hub.service';
import { utcStamp } from './fieldTime';

/**
 * A hub's pictures from `hub-<mac>/images/`, one at a time and as large as the
 * dialog allows, with a thumbnail strip to jump around and a picker to add one.
 */
export default function HubImageViewer({ macAddress }: { macAddress: string }) {
    const { data: images, isLoading, error } = useHubImages(macAddress);
    const addImage = useAddHubImage();
    const deleteImage = useDeleteHubImage();
    const fileRef = useRef<HTMLInputElement>(null);
    const [index, setIndex] = useState(0);
    /** Name of a picture just uploaded, to land on once the list catches up. */
    const [pending, setPending] = useState<string | null>(null);

    const count = images?.length ?? 0;

    useEffect(() => {
        if (!images) return;
        if (pending) {
            const at = images.findIndex((image) => image.name === pending);
            if (at >= 0) {
                setIndex(at);
                setPending(null);
                return;
            }
        }
        // Keep the cursor in range as the list changes under it.
        setIndex((i) => Math.min(i, Math.max(0, images.length - 1)));
    }, [images, pending]);

    const step = useCallback(
        (delta: number) => {
            if (count === 0) return;
            setIndex((i) => (i + delta + count) % count);
        },
        [count],
    );

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'ArrowRight') step(1);
            else if (event.key === 'ArrowLeft') step(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [step]);

    const pick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // Clear it so picking the same file again still fires a change.
        event.target.value = '';
        if (!file) return;
        addImage.mutate(
            { macAddress, file },
            { onSuccess: (response) => setPending(response.data.name) },
        );
    };

    const current = images && count > 0 ? images[Math.min(index, count - 1)] : null;

    const remove = () => {
        if (!current) return;
        if (!window.confirm(`Delete the picture "${current.name}"?`)) return;
        deleteImage.mutate({ macAddress, fileName: current.name });
    };

    return (
        <div className="flex flex-col flex-1 min-h-0 gap-2">
            {/* Toolbar */}
            <div className="flex items-center gap-3 text-xs text-[#a8b0bb] flex-shrink-0">
                {current && (
                    <>
                        <span className="tabular-nums">
                            {index + 1} / {count}
                        </span>
                        <span className="font-mono text-[#d8dde2] truncate">
                            {current.name}
                        </span>
                        <span className="text-[#6b7480] flex-shrink-0">
                            {formatSize(current.size)}
                            {current.lastModified &&
                                ` · ${utcStamp(current.lastModified, true)}`}
                        </span>
                    </>
                )}
                <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                    {addImage.isError && (
                        <span className="text-red-400">Upload failed</span>
                    )}
                    {deleteImage.isError && (
                        <span className="text-red-400">Delete failed</span>
                    )}
                    {current && (
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={deleteImage.isPending}
                            onClick={remove}
                            className="h-7 bg-[#0f141a] border-[#1e242a] text-red-400 hover:bg-[#2a1a1a] hover:text-red-300"
                        >
                            {deleteImage.isPending ? (
                                <Loader2Icon className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            ) : (
                                <Trash2Icon className="h-3.5 w-3.5 mr-1.5" />
                            )}
                            Delete
                        </Button>
                    )}
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        onChange={pick}
                        className="hidden"
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={addImage.isPending}
                        onClick={() => fileRef.current?.click()}
                        className="h-7 bg-[#0f141a] border-[#1e242a] text-[#d8dde2] hover:bg-[#1a2126] hover:text-white"
                    >
                        {addImage.isPending ? (
                            <Loader2Icon className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        ) : (
                            <ImagePlusIcon className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        Add image
                    </Button>
                </div>
            </div>

            {/* Stage */}
            <div className="relative flex-1 min-h-0 bg-[#0f141a] border border-[#1e242a] rounded flex items-center justify-center overflow-hidden">
                {isLoading ? (
                    <Skeleton className="w-full h-full bg-[#1e242a]" />
                ) : error ? (
                    <div className="text-red-400 text-sm">
                        {error.toString()}
                    </div>
                ) : current ? (
                    <img
                        key={current.name}
                        src={current.url}
                        alt={current.name}
                        className="max-h-full max-w-full object-contain"
                    />
                ) : (
                    <div className="text-sm text-[#6b7480]">
                        No images for this hub yet
                    </div>
                )}

                {count > 1 && (
                    <>
                        <StepButton side="left" onClick={() => step(-1)} />
                        <StepButton side="right" onClick={() => step(1)} />
                    </>
                )}
            </div>

            {/* Thumbnail strip */}
            {count > 1 && (
                <div className="flex-shrink-0 flex gap-2 overflow-x-auto pb-1">
                    {images?.map((image, at) => (
                        <button
                            key={image.name}
                            onClick={() => setIndex(at)}
                            title={image.name}
                            className={`h-14 w-20 flex-shrink-0 rounded overflow-hidden border transition-colors ${
                                at === index
                                    ? 'border-[#74c69d]'
                                    : 'border-[#1e242a] hover:border-[#2a323a]'
                            }`}
                        >
                            <img
                                src={image.url}
                                alt={image.name}
                                className="h-full w-full object-cover"
                            />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function StepButton({
    side,
    onClick,
}: {
    side: 'left' | 'right';
    onClick: () => void;
}) {
    const Icon = side === 'left' ? ChevronLeftIcon : ChevronRightIcon;
    return (
        <button
            onClick={onClick}
            title={side === 'left' ? 'Previous (←)' : 'Next (→)'}
            className={`absolute top-1/2 -translate-y-1/2 ${
                side === 'left' ? 'left-2' : 'right-2'
            } h-10 w-10 rounded-full flex items-center justify-center bg-[#161c22]/80 border border-[#1e242a] text-[#d8dde2] hover:bg-[#1a2126] hover:text-white`}
        >
            <Icon className="h-5 w-5" />
        </button>
    );
}

function formatSize(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(1)} KB`;
}
